import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { Box, Button, Card, CardActions, CardContent, Divider, Grid, TextField, Typography } from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { makeStyles, withStyles } from "@material-ui/core/styles";

import SimpleTooltip from "components/SimpleTooltip";

import { operations, selectors } from "modules/customer/account/settings";

const StyledCard = withStyles(() => ({
    root: {
        flexGrow: 1
    }
}))(Card);

const StyledCardContent = withStyles(() => ({
    root: {
        paddingBottom: 0
    }
}))(CardContent);

const StyledCardActions = withStyles(() => ({
    root: {
        paddingTop: 0,
        justifyContent: "flex-end"
    }
}))(CardActions);

const StyledEditButton = withStyles(theme => ({
    root: {
        color: theme.palette.tertiary.main
    }
}))(Button);

const StyledSaveButton = withStyles(theme => ({
    root: {
        marginLeft: theme.spacing(1)
    }
}))(Button);

const useStyles = makeStyles(theme => ({
    input: {
        backgroundColor: theme.palette.quaternary.main
    }
}));

const PersonalInfo = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const personalInfo = useSelector(state => selectors.personalInfo(state));
    const editPersonalInfoVisibility = useSelector(state => selectors.editPersonalInfoVisibility(state));
    const canEditPersonalInfo = useSelector(state => selectors.canEditPersonalInfo(state));

    const handleEditClick = () => {
        dispatch(operations.showEditPersonalInfo());
    };

    const handleSaveClick = () => {
        dispatch(operations.editPersonalInfo());
    };

    const handleCancelClick = () => {
        dispatch(operations.hideEditPersonalInfo());
    };

    const handleFirstNameChange = (event) => {
        const firstName = event.target.value;
        const newPersonalInfo = { ...personalInfo, firstName, errors: { ...personalInfo.errors, firstName: "" } };
        dispatch(operations.setPersonalInfo(newPersonalInfo));
    };

    const handleLastNameChange = (event) => {
        const lastName = event.target.value;
        const newPersonalInfo = { ...personalInfo, lastName, errors: { ...personalInfo.errors, lastName: "" } };
        dispatch(operations.setPersonalInfo(newPersonalInfo));
    };

    const handlePhoneNumberChange = (event) => {
        const phoneNumber = event.target.value;
        const newPersonalInfo = { ...personalInfo, phoneNumber, errors: { ...personalInfo.errors, phoneNumber: "" } };
        dispatch(operations.setPersonalInfo(newPersonalInfo));
    };

    return (
        <Box height="100%" display="flex" flexDirection="column" data-cy="pages-customer-account-settings-personal-info">
            <Box color="text.disabled">
                <Typography variant="subtitle1" component="div" gutterBottom>
                    Personal information
                </Typography>
            </Box>
            <StyledCard elevation={0}>
                <StyledCardContent>
                    <Typography variant="h4" component="div">
                        Name
                    </Typography>
                    <Box color="text.disabled" paddingTop={1}>
                        {editPersonalInfoVisibility.isVisible ? (
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        label="First name"
                                        variant="outlined"
                                        size="small"
                                        color="secondary"
                                        margin="dense"
                                        value={personalInfo.firstName}
                                        onChange={handleFirstNameChange}
                                        error={!!personalInfo.errors.firstName}
                                        helperText={personalInfo.errors.firstName}
                                        required
                                        fullWidth
                                        InputProps={{ classes: { input: classes.input } }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Last name"
                                        variant="outlined"
                                        size="small"
                                        color="secondary"
                                        margin="dense"
                                        value={personalInfo.lastName}
                                        onChange={handleLastNameChange}
                                        error={!!personalInfo.errors.lastName}
                                        helperText={personalInfo.errors.lastName}
                                        required
                                        fullWidth
                                        InputProps={{ classes: { input: classes.input } }}
                                    />
                                </Grid>
                            </Grid>
                        ) : (
                            <Typography variant="subtitle1" component="div">
                                {personalInfo.firstName} {personalInfo.lastName}
                            </Typography>
                        )}
                    </Box>
                    <Box paddingY={2}>
                        <Divider />
                    </Box>
                    <Box display="flex" alignItems="center">
                        <Typography variant="h4" component="div">
                            Contact number
                        </Typography>
                        &nbsp;&nbsp;
                        <SimpleTooltip
                            placement="right"
                            title={
                                <Typography variant="body1" component="div">
                                    Changes to your contact number do not affect your multi-factor authentication for login via KPMG Gateway
                                </Typography>
                            }
                        >
                            <InfoOutlinedIcon color="disabled" cursor="pointer" />
                        </SimpleTooltip>
                    </Box>
                    <Box color="text.disabled" paddingTop={1}>
                        {editPersonalInfoVisibility.isVisible ? (
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Phone number"
                                        variant="outlined"
                                        size="small"
                                        color="secondary"
                                        margin="dense"
                                        value={personalInfo.phoneNumber}
                                        onChange={handlePhoneNumberChange}
                                        error={!!personalInfo.errors.phoneNumber}
                                        helperText={personalInfo.errors.phoneNumber}
                                        fullWidth
                                        InputProps={{ classes: { input: classes.input } }}
                                    />
                                </Grid>
                            </Grid>
                        ) : (
                            <Typography variant="subtitle1" component="div" gutterBottom>
                                {personalInfo.phoneNumber}
                            </Typography>
                        )}
                    </Box>
                </StyledCardContent>
                <StyledCardActions>
                    {editPersonalInfoVisibility.isVisible ? (
                        <>
                            <Button
                                variant="text"
                                size="medium"
                                color="default"
                                disableElevation
                                onClick={handleCancelClick}
                            >
                                Cancel
                            </Button>
                            <StyledSaveButton
                                variant="contained"
                                size="small"
                                color="secondary"
                                disableElevation
                                disabled={!canEditPersonalInfo}
                                onClick={handleSaveClick}
                            >
                                Save
                            </StyledSaveButton>
                        </>
                    ) : (
                        <StyledEditButton
                            variant="text"
                            size="medium"
                            color="default"
                            disableElevation
                            onClick={handleEditClick}
                        >
                            Edit
                        </StyledEditButton>
                    )}
                </StyledCardActions>
            </StyledCard>
        </Box>
    );
};

export default PersonalInfo;
